import { IAppState } from './../../store/state/app.state';
import { ReturnTypes, MODEL_HOLDINGS_COLUMNS, IAssetPerformanceData, NotSupportedReason } from './../../models/performance';
import { PercentagePipe } from 'src/app/utils/percentage.pipe';
import { DatePipe, DecimalPipe } from '@angular/common';
import { BaseXLSReportService } from './base.excel-report.service';
import { Store } from '@ngrx/store';
import { Injectable } from "@angular/core";
import * as Excel from "exceljs";

@Injectable()
export class PerformanceExcelReportService extends BaseXLSReportService {

    constructor(public store: Store<IAppState>, public decimalPipe: DecimalPipe, public percentagePipe: PercentagePipe, public datePipe: DatePipe) {
        super(store, decimalPipe, percentagePipe, datePipe);
    }
    exportPerformanceToExcel() {
        const workbook: Excel.Workbook = new Excel.Workbook();
        workbook.creator = 'Adminis';
        workbook.created = new Date();
        workbook.modified = new Date();
        if (this.portfolioReturnsData !== undefined && this.portfolioReturnsData.length > 0 && this.portfolioSnapshotData !== undefined && this.portfolioSnapshotData.length > 0) {
            const performanceReturnsSheet: Excel.Worksheet = workbook.addWorksheet('Returns');
            this.createPerformanceReturnsSheet(performanceReturnsSheet);
        }

        if (this.selectedEntities.length === 1 && this.portfolioBreakdownData !== undefined && this.portfolioBreakdownData.length > 0) {
            const performanceBreakdownSheet: Excel.Worksheet = workbook.addWorksheet('Breakdown');
            this.createPerformanceBreakdownSheet(performanceBreakdownSheet);
        }

        const date = this.datePipe.transform(new Date(this.selectedDate), 'd MMM yyyy');
        const reportFileName = this.selectedEntities[0].Name + '_Performance_' + date + '.xlsx';

        this.writeSheet(workbook, reportFileName);
    }

    exportAssetPerformanceToExcel(contributionToReturnEnabled: boolean, assetClassification: boolean) {
        const workbook: Excel.Workbook = new Excel.Workbook();

        workbook.creator = 'Adminis';
        workbook.created = new Date();
        workbook.modified = new Date();

        if (this.performanceByAssetData !== undefined && this.performanceByAssetData.length > 0) {
            const assetPerformanceSheet: Excel.Worksheet = workbook.addWorksheet('Performance by Asset');
            this.createAssetPerformanceSheet(assetPerformanceSheet, contributionToReturnEnabled, assetClassification);
        }

        const date = this.datePipe.transform(new Date(this.selectedDate), 'd MMM yyyy');
        const reportFileName = this.selectedEntities[0].Name + '_Performance_By_Asset_' + date + '.xlsx';

        this.writeSheet(workbook, reportFileName);
    }

    createAssetPerformanceSheet(
        worksheet: Excel.Worksheet, 
        contributionToReturnEnabled: boolean, 
        assetClassification: boolean) {
        const headerRowFont = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: '000000' }
            },
            dataRowFont = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: false,
                color: { argb: '000000' }
            },
            extraColumns = [
                {
                    label: 'Opening value',
                    width: 14,
                },
                {
                    label: 'Purchases / Sales',
                    width: 14,
                },
                {
                    label: 'Realised gains',
                    width: 14,
                },
                {
                    label: 'Gross income',
                    width: 14,
                },
                {
                    label: 'Expenses',
                    width: 14,
                },
                {
                    label: 'Percent return per year',
                    width: 14,
                },
                {
                    label: 'Cumulative percent return',
                    width: 14,
                },
                {
                    label: 'Currency impact per year',
                    width: 14,
                },
                {
                  label: 'Cumulative currency impact',
                  width: 14,
                },
            ]

        let rowIndex = 1;
        let columns = [];
        const columnWidths = {
            name: 40,
            allocation: 24,
            market_value: 14,
            weight: 14,
            performance: 14
        }

        let holdingsColumns = MODEL_HOLDINGS_COLUMNS;
        if (assetClassification) {
            holdingsColumns = holdingsColumns.filter((column) => column.name !== "name");
        }
        if (!contributionToReturnEnabled) {
            holdingsColumns = holdingsColumns
                .filter((column) => 
                    column.name !== "contribution_to_return" && 
                    column.name !== "opening_weight");
        }
        
        holdingsColumns.forEach((column) => {
            columns.push({
                header: column.label,
                key: '',
                width: columnWidths[column.name]
            });
        });
        extraColumns.forEach((column) => {
            columns.push({
                header: column.label,
                key: '',
                width: column.width
            })
        });

        worksheet.columns = columns;

        worksheet.getRow(rowIndex).font = headerRowFont;
        rowIndex++;

        const assetPerformance  = assetClassification 
            ? this.performanceByAssetClassificationData 
            : this.performanceByAssetData;

        assetPerformance.forEach((asset) => {
            // We don't filter out classifications as only some assets might not be supported in the classification.
            if (asset.IsSecuritySupported || asset.AssetReturn.NotSupportedReason === NotSupportedReason.Cash || assetClassification) {
                worksheet.addRow([
                    // No name if we're generating for asset classification.
                    ...(assetClassification ? [] : [asset.Name]),
                    asset.ManagerAssetClassification,
                    asset.ClosingMarketValue.Value,
                    asset.PortfolioWeight,
                    ...(asset.AssetReturn.NotSupportedReason !== NotSupportedReason.Cash ? [ asset.AssetReturn.TotalReturn ] : [ null ]),
                    ...(contributionToReturnEnabled ? [asset.ContributionToReturn] : []),
                    ...(contributionToReturnEnabled ? [asset.OpeningWeight] : []),
                    asset.OpeningMarketValue.Value,
                    asset.PurchaseSales.Value,
                    asset.RealisedGains.Value,
                    asset.GrossIncome.Value,
                    asset.Expenses.Value,
                    asset.AssetReturn.DeAnnualisedTotalReturn,
                    ...(asset.AssetReturn.NotSupportedReason !== NotSupportedReason.Cash ? [ asset.AssetReturn.TotalReturn ] : [ null ]),
                    asset.AssetReturn.DeAnnualisedCurrencyImpact,
                    asset.AssetReturn.CurrencyImpact,
                ]);
                // No name column if classification.
                let column = assetClassification ? 2 : 3;
                worksheet.getRow(rowIndex).font = dataRowFont;
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2});
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                if (contributionToReturnEnabled) {
                    worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                    worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                }
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getNumberFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                worksheet.getCell(rowIndex, column++).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                rowIndex++;
            }
        });
    }

    createPerformanceReturnsSheet(worksheet: Excel.Worksheet) {

        let rowIndex = 0;

        worksheet.columns = [
            { header: 'Trailing Returns', key: '', width: 30 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 }
        ];

        const row = worksheet.getRow(1);
        row.font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };

        worksheet.addRow([
            'Return Type',
            '1M',
            '3M',
            '12M',
            '3Y',
            '5Y',
            'Inception'
        ]);

        rowIndex += 2;
        const currency = this.performanceState.performanceData.currency;
        for (let i = 0; i < worksheet.columns.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex.toString());
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };
        }

        rowIndex++;

        const monthReturn = this.portfolioSnapshotData.find( item => item.caption === 'Month');
        const quarterReturn = this.portfolioSnapshotData.find( item => item.caption === 'Quarter');
        const yearReturn = this.portfolioSnapshotData.find( item => item.caption === '12 Months');
        const threeYearReturn = this.portfolioSnapshotData.find( item => item.caption === '3 Years');
        const fiveYearReturn = this.portfolioSnapshotData.find( item => item.caption === '5 Years');
        const inceptionReturn = this.portfolioSnapshotData.find( item => item.caption === 'Inception');

        worksheet.addRow([
            (this.selectedReturnType === ReturnTypes.XIRR) ? 'IRR' : 'TWR',
            this.createReturnRowItem(monthReturn),
            this.createReturnRowItem(quarterReturn),
            this.createReturnRowItem(yearReturn),
            this.createReturnRowItem(threeYearReturn),
            this.createReturnRowItem(fiveYearReturn),
            this.createReturnRowItem(inceptionReturn)
        ]);

        for (let i = 1; i < 8; i++) {
            worksheet.getCell(rowIndex, i).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
        }

        if (this.selectedReturnType === ReturnTypes.XIRR) {
        worksheet.addRow([
          "IRR",
          monthReturn && monthReturn.returnInCurrency !== undefined
            ? Number(monthReturn.returnInCurrency.toFixed(2)).toLocaleString() +
              " " +
              currency
            : "",
          quarterReturn && quarterReturn.returnInCurrency !== undefined
            ? Number(
                quarterReturn.returnInCurrency.toFixed(2)
              ).toLocaleString() +
              " " +
              currency
            : "",
          yearReturn && yearReturn.returnInCurrency !== undefined
            ? Number(yearReturn.returnInCurrency.toFixed(2)).toLocaleString() +
              " " +
              currency
            : "",
          threeYearReturn && threeYearReturn.returnInCurrency !== undefined
            ? Number(threeYearReturn.returnInCurrency.toFixed(2)).toLocaleString() +
              " " +
              currency
            : "",
          fiveYearReturn && fiveYearReturn.returnInCurrency !== undefined
            ? Number(
                fiveYearReturn.returnInCurrency.toFixed(2)
              ).toLocaleString() +
              " " +
              currency
            : "",
          inceptionReturn && inceptionReturn.returnInCurrency !== undefined
            ? Number(
                inceptionReturn.returnInCurrency.toFixed(2)
              ).toLocaleString() +
              " " +
              currency
            : ""
        ]);
      }

        for (let j = 0; j < worksheet.columns.length; j++) {
            const rowNumberString = rowIndex.toString();
            worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };
        }

        rowIndex++;

        this.createPerformanceReturnsTable(worksheet, rowIndex);
    }

    createPerformanceReturnsTable(worksheet: Excel.Worksheet, rowIndex: number) {

        worksheet.addRow([]);
        rowIndex++;
        worksheet.addRow(['Returns']).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };
        rowIndex++;
        const returnsHeaders = (this.selectedReturnType === ReturnTypes.XIRR) ? [
            '',
            'Date',
            'Period',
            'IRR',
            'Return In Currency'
        ] : [
            '',
            'Date',
            'Period',
            'TWR',
            'Cum TWR'
        ];

        worksheet.addRow(returnsHeaders);
        for (let i = 0; i < returnsHeaders.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex.toString());
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };
        }
        rowIndex++;

        for (let i = 0; i < this.portfolioReturnsData.length; i++) {
            const returnsDataItem = this.portfolioReturnsData[i];
            const date = this.datePipe.transform(new Date(returnsDataItem.date), 'd MMM yyyy');
            const IRR = (this.selectedReturnType === ReturnTypes.XIRR) ? this.percentageNumber(returnsDataItem.return[0] , 4) : '';
            const TWR = (this.selectedReturnType === ReturnTypes.TWR) ? this.percentageNumber(returnsDataItem.return[0] , 4) : '';
            const CumTWR = (this.selectedReturnType === ReturnTypes.TWR) ? this.decimalNumber(returnsDataItem.return[1], 2) : '';
            const ReturnInCurrency = returnsDataItem.returnInCurrency
              ? Number(
                  returnsDataItem.returnInCurrency.Value
                ).toLocaleString() +
                " " +
                returnsDataItem.returnInCurrency.CurrencyCode
              : "";
            const returnDataRow = (this.selectedReturnType === ReturnTypes.XIRR) ?
            [
                'Total',
                date,
                this.selectedReturnPeriod,
                IRR,
                ReturnInCurrency
            ]
            :
            [
                'Total',
                date,
                this.selectedReturnPeriod,
                TWR,
                CumTWR
            ];
            worksheet.addRow(returnDataRow);
            rowIndex++;
            worksheet.getCell(rowIndex, 4).style.numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
        }

        for (let j = 0; j < returnsHeaders.length; j++) {
            const rowNumberString = (rowIndex - 1).toString();
            worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };
        }

    }

    createReturnRowItem(returnItem: any) {
        if (returnItem && returnItem.return !== undefined) {
            return this.percentageNumber(returnItem.return, 4);
        }
        return '';

    }

    createPerformanceBreakdownSheet(worksheet: Excel.Worksheet) {
        let rowIndex = 0;
        worksheet.columns = [
            { header: 'Return Breakdown', key: '', width: 30 },
            { header: '', key: '', width: 20 }
        ];

        const row = worksheet.getRow(1);
        row.font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };

        worksheet.addRow([
            '',
            'value'
        ]);

        rowIndex += 2;

        for (let i = 0; i < worksheet.columns.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex.toString());
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };
        }

        rowIndex++;

        this.createPerformanceBreakdownTable(worksheet, rowIndex);

    }

    createPerformanceBreakdownTable(worksheet: Excel.Worksheet, rowIndex: number) {

        worksheet.addRow(['Opening Value', this.decimalNumber(this.openingValue, 2)]).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };
        worksheet.getCell(rowIndex, 2).numFmt = this.getNumberFormat({includeSeparator: false, decimalPlaces: 2});

        for (let j = 0; j < worksheet.columns.length; j++) {
            const rowNumberString = rowIndex.toString();
            worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };
        }

        rowIndex++;

        for (let i = 0; i < this.portfolioBreakdownData.length; i++) {
            const breakdownDataItem = this.portfolioBreakdownData[i];
            const caption = breakdownDataItem.data.caption;
            const value = this.decimalNumber(breakdownDataItem.data.value, 2);

            worksheet.addRow([caption , value]).font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: '000000' }
            };
            worksheet.getCell(rowIndex, 2).numFmt = this.getNumberFormat({ includeSeparator: false, decimalPlaces: 2 });

            if (breakdownDataItem.children && breakdownDataItem.children.length > 0) {
                rowIndex++;
                for (let j = 0; j < breakdownDataItem.children.length ; j++) {
                    const breakdownChild = breakdownDataItem.children[j];
                    const childCaption = breakdownChild.data.caption;
                    const childValue = this.decimalNumber(breakdownChild.data.value, 2);
                    worksheet.addRow(['   ' + childCaption , childValue]).font = {
                        name: 'Segoe UI',
                        family: 2,
                        size: 9,
                        underline: false,
                        bold: false,
                        color: { argb: '000000' }
                    };
                    worksheet.getCell(rowIndex, 2).numFmt = this.getNumberFormat({ includeSeparator: false, decimalPlaces: 2 });
                    rowIndex++;
                }
                for (let j = 0; j < worksheet.columns.length; j++) {
                    const rowNumberString = (rowIndex - 1).toString();
                    worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                        bottom: { style: 'thin', color: { argb: '000000' } }
                    };
                }
            } else {
                for (let j = 0; j < worksheet.columns.length; j++) {
                    const rowNumberString = rowIndex.toString();
                    worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                        bottom: { style: 'thin', color: { argb: '000000' } }
                    };
                }
                rowIndex++;
            }
        }

        worksheet.addRow(['Closing Value', this.decimalNumber(this.closingValue, 2)]).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };

        for (let j = 0; j < worksheet.columns.length; j++) {
            const rowNumberString = (rowIndex - 1).toString();
            worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };
        }

    }

}
